import { captureException } from '@sentry/browser';
import { omit } from 'lodash';

import { RESET_ALL as RESET_ON_LOGOUT, danger, success } from 'redux-store/reducers/common-reducer';
import { hideLoading, showLoading } from 'redux-store/reducers/loading-reducer';

import { API, destroy, get, patch, post, put } from 'utils/api';
import { logError } from 'utils/log';
import { createReducer } from 'utils/redux';
import { __t, applyTranslations } from 'utils/language';

import {
  addRealmOutfitResource,
  getCommonErrorMessage,
  handleVideoUpload,
  removeRealmOutfitResource
} from 'modules/realm-outfits/reducers/realm-outfits';

import type {
  Action,
  CampaignOutfit,
  CampaignType,
  DeliverableNote,
  RealmOutfitItemResource,
  ThunkAction
} from 'types';
import {
  RealmOutfitBrandForm
} from 'modules/realm-outfits/types/RealmOutfit.type';

applyTranslations(require('./campaign-outfits.i18n.json'));

const LOADING = 'campaignOutfits/LOADING';
const LOADED = 'campaignOutfits/LOADED';
const RESET = 'campaignOutfits/RESET';
const LOAD_NOTES = 'campaignOutfits/LOAD_NOTES';
const LOAD_DELIVERABLE_ID = 'campaignOutfits/LOAD_DELIVERABLE_ID';

const initialState = {
  outfits: [],
  notes: [],
  campaignDeliverableId: null,
  isLoading: false
};

export const resetCampaignOutfits = (): Action => ({
  type: RESET
});

const setLoadingStatus = (status: boolean): Action => ({
  type: LOADING,
  payload: { status }
});

const campaignOutfitsLoaded = (data: Array<CampaignOutfit>): Action => ({
  type: LOADED,

  payload: {
    data
  }
});

const campaignNotesLoaded = (data: Array<DeliverableNote>): Action => ({
  type: LOAD_NOTES,

  payload: {
    data
  }
});

export function fetchCampaignOutfits(
  campaignId: number,
  campaignType?: CampaignType
): ThunkAction {
  const url = campaignType === 'ccm_creatorless'
    ? API.END_POINTS.CAMPAIGNS.getCreatorlessOutfits(campaignId)
    : API.END_POINTS.CAMPAIGNS.getOutfits(campaignId);

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    try {
      const response = await get(url, {}, { suppressGlobalNotification: true });

      dispatch(campaignOutfitsLoaded(response.data));
      dispatch(setLoadingStatus(false));
    } catch (error) {
      logError(error);

      dispatch(setLoadingStatus(false));
      throw error;
    }
  };
}

export function submitCreatorlessOutfits(
  campaignId: number,
  deliverableId: number,
  data: Omit<RealmOutfitBrandForm, 'id'>,
  addedSKUs: Array<RealmOutfitItemResource>,
  removedSKUs: Array<number>,
  outfitId?: number
): ThunkAction {
  const url = outfitId ? API.END_POINTS.CAMPAIGNS.updateCreatorlessOutfit(campaignId, outfitId) :
    API.END_POINTS.CAMPAIGNS.submitCreatorlessOutfit(campaignId);
  const method = outfitId ? put : post;

  return async (dispatch) => {
    dispatch(showLoading(url));

    try {
      if (outfitId && addedSKUs.length > 0) {
        await Promise.all(addedSKUs.map(sku => (
          dispatch(addRealmOutfitResource(outfitId, sku, deliverableId))
        )));
      }

      if (outfitId && removedSKUs.length > 0) {
        await Promise.all(removedSKUs.map(skuId => (
          dispatch(removeRealmOutfitResource(outfitId, skuId, deliverableId))
        )));
      }

      const response = await method(url, { data: omit(data, 'video', 'status') });
      const outfit = response.data;

      if ((data?.video as any)?.file) {
        await dispatch(handleVideoUpload((data.video as any), false, outfit.id, deliverableId));
      }

      dispatch(fetchCampaignOutfits(campaignId, 'ccm_creatorless'));

      return true;
    } catch (error) {
      const errorResponse = error?.response?.data;
      const errMsg = errorResponse?.detail
        || __t(`campaign_outfits_reducer.errors.${errorResponse?.code}`)
        || __t('campaign_outfits_reducer.errors.generic');

      dispatch(danger(errMsg));
      captureException(error);

      return false;
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function getCreatorlessApprovalNote(campaignId: number) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.creatorlessNotes(campaignId);

    try {
      const response = await get(url);

      dispatch(campaignNotesLoaded(response.data.approvalNotes));
    } catch (error) {
      captureException(error);
    }
  };
}

export function sendCreatorlessApprovalNote(
  campaignId: number,
  noteValue: string,
  authorType: Exclude<DeliverableNote['author']['type'], 'external_client'>
) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.creatorlessNotes(campaignId);

    try {
      const response = await put(url, {
        data: { note: {
          text: noteValue,
          authorType
        } }
      });

      dispatch(campaignNotesLoaded(response.data.approvalNotes));
    } catch (error) {
      captureException(error);
    }
  };
}

export function editApprovalNote(campaignId: number, noteId: number, noteValue?: string) {
  return async (dispatch) => {
    const url = API.END_POINTS.DELIVERABLE.editApprovalNote(noteId);

    dispatch(showLoading(url));
    try {
      const requestMethod = noteValue ? patch : destroy;

      await requestMethod(url, {
        data: { text: noteValue }
      }, { suppressGlobalNotification: true });

      dispatch(getCreatorlessApprovalNote(campaignId));
    } catch (error) {
      dispatch(danger(__t('campaign_outfits_reducer.note_update_failed')));
      logError(error);
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function createCampaignOutfits(campaignId: number, creatorIds: Array<number>): ThunkAction {
  const url = API.END_POINTS.CAMPAIGNS.createOutfits(campaignId);

  return async (dispatch) => {
    dispatch(setLoadingStatus(true));

    try {
      await post(url, { data: { creatorIds } }, { suppressGlobalNotification: true });

      dispatch(success(__t('campaign_outfits_reducer.outfit_add_success')));
      dispatch(fetchCampaignOutfits(campaignId, 'ccm_creatorless'));
    } catch (error) {
      logError(error);
      dispatch(danger(__t('campaign_outfits_reducer.outfit_add_error')));
      throw error;
    } finally {
      dispatch(setLoadingStatus(false));
    }
  };
}

export function declineOutfit(
  campaignId: number,
  outfitId: number,
  deliverableId,
  declinedReason: string): ThunkAction {
  const url = API.END_POINTS.REALM_OUTFITS.declineOutfit(outfitId, deliverableId);

  return async (dispatch) => {
    try {
      dispatch(showLoading(url));

      await put(url, { data: { declinedReason } }, {
        suppressGlobalNotification: true
      });

      dispatch(fetchCampaignOutfits(campaignId, 'ccm_creatorless'));

      dispatch(success(__t('campaign_outfits_reducer.message_declined')));

      return true;
    } catch (error) {
      logError(error);
      dispatch(danger(__t('campaign_outfits_reducer.message_failed')));

      return false;
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export function publishUnpublishOutfit(
  campaignId: number,
  outfitId: number,
  deliverableId: number,
  type?: 'publish' | 'unpublish'): ThunkAction {
  const url = type === 'unpublish' ?
    API.END_POINTS.REALM_OUTFITS.unpublishOutfit(outfitId, deliverableId) :
    API.END_POINTS.REALM_OUTFITS.publishOutfit(outfitId, deliverableId);

  return async (dispatch) => {
    try {
      dispatch(showLoading(url));

      const response = await put(url, {}, { suppressGlobalNotification: true });
      const updatedOutfit = response.data;

      if (updatedOutfit.publishedError) {
        dispatch(danger(updatedOutfit.publishedError));

        return false;
      }

      dispatch(fetchCampaignOutfits(campaignId, 'ccm_creatorless'));

      dispatch(success(__t('campaign_outfits_reducer.message_publish')));

      return true;
    } catch (error) {
      const errorCode = error.response?.data?.code;
      const errorDetail = error.response?.data?.detail;

      const errorMsg = errorCode ?
        __t(`campaign_outfits_reducer.errors.${errorCode}`) : '';

      logError(error);

      dispatch(danger(errorMsg || errorDetail || getCommonErrorMessage(error)));

      return false;
    } finally {
      dispatch(hideLoading(url));
    }
  };
}

export const fetchCampaignDeliverables = (campaignId: number) => {
  const url = API.END_POINTS.CAMPAIGNS.getCampaignDeliverables(campaignId);

  return async (dispatch) => {
    try {
      const response = await get(url);

      dispatch({
        type: LOAD_DELIVERABLE_ID,
        payload: { campaignDeliverableId: response.data.data[0].id }
      });
    } catch (e) {
      dispatch(danger(__t('campaign_outfits_reducer.errors.generic')));
    }
  };
};

export default createReducer<any, any>('campaignOutfits', initialState, {
  [RESET_ON_LOGOUT]: () => initialState,
  [RESET]: () => initialState,
  [LOADING]: (state, { status }) => ({
    ...state,
    isLoading: status
  }),
  [LOADED]: (state, { data }) => ({
    ...state,
    outfits: data,
    isLoading: false
  }),
  [LOAD_NOTES]: (state, { data }) => ({
    ...state,
    notes: data
  }),
  [LOAD_DELIVERABLE_ID]: (state, { campaignDeliverableId }) => ({
    ...state,
    campaignDeliverableId
  })
});
